// Unauthenticated /api/health probe. Shared by the header's connection badge
// and the stale-bundle notice so both read one cached response.

import { useQuery } from '@tanstack/react-query';

export interface HealthResponse {
  status: string;
  service: string;
  /** Running server version — the same string the SPA is stamped with at build. */
  version: string;
  /** Active storage backend — "rdf" (Fuseki) or "qros". */
  backend?: string;
  oidc_jwks_age_seconds?: number;
  /** The index in states and numbers. `status` is `degraded` when the VDB is
   *  deferred/failed or the build breaker is open; the process still serves. */
  index?: {
    vdb: 'pending' | 'building' | 'ready' | 'deferred' | 'failed';
    /** VDB-backed reads answer right now (the previous index serves through a
     *  merge and most of a full build; false only before the first index and
     *  in a rebuild's index phase). */
    serving: boolean;
    detail?: string;
    semantic?: { state: 'building' | 'ready' | 'deferred'; detail?: string | null };
    breaker: { open: boolean; attempts: number };
    memory: { rss_mb?: number; limit_mb?: number };
  };
}

/** Polls every five minutes so a long-lived tab notices a deploy. */
export function useHealth() {
  return useQuery<HealthResponse>({
    queryKey: ['health'],
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const res = await fetch('/api/health');
      if (!res.ok) throw new Error(`health ${res.status}`);
      return (await res.json()) as HealthResponse;
    },
    retry: 1,
  });
}
